import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { user, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error(t('contact_empty_message'));
      return;
    }

    setLoading(true);
    try {
      if (user) {
        // Logged in user - send via API
        await axios.post(`${API}/support/messages`, 
          { message: formData.message },
          { headers: getAuthHeaders() }
        );
        toast.success(t('contact_success'));
      } else {
        // Guest - just show success (in production, you'd send via email service)
        toast.success(t('contact_success_guest'));
      }
      setFormData({ ...formData, message: '' });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error(t('contact_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-foreground mb-4">
            {t('contact_title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contact_subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground mb-1">{t('contact_email')}</h3>
                      <p className="text-muted-foreground text-sm">{t('contact_email_value')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground mb-1">{t('contact_live_chat')}</h3>
                      <p className="text-muted-foreground text-sm">{t('contact_live_chat_value')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground mb-1">{t('contact_response_time')}</h3>
                      <p className="text-muted-foreground text-sm">{t('contact_response_time_value')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-border/40">
                <CardHeader>
                  <CardTitle className="font-heading">{t('contact_form_title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('contact_name')}</Label>
                        <Input
                          id="name"
                          placeholder={t('contact_name_placeholder')}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          disabled={!!user}
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('contact_email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder={t('contact_email_placeholder')}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          disabled={!!user}
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">{t('contact_message')}</Label>
                      <Textarea
                        id="message"
                        placeholder={t('contact_message_placeholder')}
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full sm:w-auto rounded-full px-8"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                    >
                      {loading ? t('contact_sending') : t('contact_submit')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
